import { useQuery } from '@tanstack/react-query'
import { BookOpen, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Badge, type BadgeProps } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { subjectsQuery, useDeleteSubject } from '@/features/academic/api'
import { DeleteDialog } from '@/features/academic/components/DeleteDialog'
import { SubjectDialog } from '@/features/academic/components/SubjectDialog'
import type { Subject, SubjectType } from '@/features/academic/types'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { humanize } from '@/utils/format'

const typeTone: Record<SubjectType, NonNullable<BadgeProps['tone']>> = {
  theory: 'brand',
  practical: 'success',
  both: 'info',
}

function Swatch({ color }: { color: string | null }) {
  return color ? (
    <span
      className="block size-4 shrink-0 rounded-full shadow-xs ring-2 ring-surface"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
  ) : (
    <span
      className="block size-4 shrink-0 rounded-full border border-dashed border-line-strong"
      aria-hidden="true"
    />
  )
}

export function SubjectsPage() {
  const { t } = useTranslation('academic')
  useDocumentTitle('Subjects')
  const subjects = useQuery(subjectsQuery())
  const remove = useDeleteSubject()
  const { can } = useAuth()
  const canManage = can('academic.manage')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Subject | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Subject | null>(null)

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!subjects.data || !q) return subjects.data
    return subjects.data.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.code?.toLowerCase().includes(q) ?? false),
    )
  }, [subjects.data, search])

  const actions = (s: Subject) => (
    <div className="flex justify-end gap-1">
      <IconButton
        label={t('Edit {{name}}', { name: s.name })}
        icon={<Pencil />}
        size="icon-sm"
        onClick={() => setEditing(s)}
      />
      <IconButton
        label={t('Delete {{name}}', { name: s.name })}
        icon={<Trash2 />}
        size="icon-sm"
        className="hover:text-danger-600"
        onClick={() => setDeleting(s)}
      />
    </div>
  )

  const columns: Column<Subject>[] = [
    {
      key: 'name',
      header: t('Subject'),
      cell: (s) => (
        <span className="flex items-center gap-3">
          <Swatch color={s.color} />
          <span className="font-medium text-ink">{s.name}</span>
        </span>
      ),
    },
    {
      key: 'code',
      header: t('Code'),
      cell: (s) =>
        s.code ? <span className="font-mono text-xs">{s.code}</span> : <span className="text-subtle">—</span>,
    },
    {
      key: 'type',
      header: t('Type'),
      cell: (s) => (
        <Badge tone={typeTone[s.type]} size="sm">
          {humanize(s.type)}
        </Badge>
      ),
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: actions,
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader
        title={t('Subjects')}
        description={t('Everything taught at the school. Add subjects to a class from its Curriculum tab.')}
        breadcrumbs={[{ label: t('Academic') }, { label: t('Subjects') }]}
        homeHref="/admin"
        actions={
          <Can permission="academic.manage">
            <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('New subject')}
            </Button>
          </Can>
        }
      />
      <Card>
        <FilterBar
          actions={
            subjects.data ? (
              <span className="text-xs text-muted tabular">
                {t('{{shown}} of {{total}}', { shown: rows?.length ?? 0, total: subjects.data.length })}
              </span>
            ) : null
          }
        >
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('Name or code')}
            label={t('Search subjects')}
          />
        </FilterBar>
        <DataTable
          caption={t('Subjects')}
          columns={columns}
          rows={rows}
          getRowId={(s) => s.id}
          loading={subjects.isPending}
          error={subjects.error}
          onRetry={() => void subjects.refetch()}
          skeletonRows={6}
          mobileCard={(s) => (
            <div className="flex items-center gap-3">
              <Swatch color={s.color} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{s.name}</p>
                <p className="text-xs text-muted">
                  {s.code ? <span className="font-mono">{s.code}</span> : t('No code')} · {humanize(s.type)}
                </p>
              </div>
              {canManage ? actions(s) : null}
            </div>
          )}
          empty={
            <EmptyState
              icon={<BookOpen />}
              title={search ? t('No subjects match your search') : t('No subjects yet')}
              description={
                search ? t('Try a different name or code.') : t('Create the subjects your school teaches.')
              }
              compact
            />
          }
        />
      </Card>

      <SubjectDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        subject={editing === 'new' ? null : editing}
      />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(s) => t('Delete {{name}}?', { name: s.name })}
        description={() =>
          t('It will also be removed from every class curriculum that includes it. This cannot be undone.')
        }
        onDelete={(s) => remove.mutateAsync(s.id)}
        successMessage={(s) => t('{{name}} deleted', { name: s.name })}
      />
    </>
  )
}
